import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';
import { Router } from '@angular/router';
import { RoutesKeys } from 'src/app/utils/routes';

@Component({
  selector: 'app-reward-card',
  templateUrl: './reward-card.component.html',
  styleUrls: ['./reward-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Reward card molecule
 */
export class RewardCardComponent implements AfterViewInit {
  @ViewChild('rewardNameRef') rewardNameRef!: ElementRef<HTMLElement>;
  @ViewChild('rewardTypeRef') rewardTypeRef!: ElementRef<HTMLElement>;

  @Input() reward!: RewardResponseDTO;
  @Input() showDefaultImage? = false;
  @Input() isRewardScreen? = false;
  @Input() showActions? = true;
  @Input() isInfoModal? = false;
  @Input() deleteHandler?: (reward: RewardResponseDTO) => void;
  @Input() availableFamilies!: any[];
  @Input() availableBusinessLevels!: any;

  @Output() removeReward = new EventEmitter<RewardResponseDTO>();
  @Output() cardClick = new EventEmitter<RewardResponseDTO>();
  @Output() closeDialog = new EventEmitter<boolean>();

  isLoading = false;
  isRewardNameOverflowing = false;
  isRewardTypeOverflowing = false;

  /**
   * Constructor
   */
  constructor(
    private readonly rewardService: RewardService,
    private readonly snackBarService: SnackBarService,
    private readonly dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  /**
   * AfterViewInit
   */
  ngAfterViewInit(): void {
    this.checkRewardContentOverflow();
  }

  /**
   * Checks whether the reward name and type elements have vertical overflow.
   * Sets corresponding flags if the content exceeds the visible area.
   */
  checkRewardContentOverflow(): void {
    const rewardNameElement = this.rewardNameRef.nativeElement;
    const rewardTypeElement = this.rewardTypeRef.nativeElement;

    this.isRewardNameOverflowing =
      rewardNameElement.scrollHeight > rewardNameElement.clientHeight;

    this.isRewardTypeOverflowing =
      rewardTypeElement.scrollHeight > rewardTypeElement.clientHeight;

    this.cdr.detectChanges();
  }

  /**
   * Opens a modal to confirm the deletion of the reward.
   * @param {function} deleteFallback
   */
  deleteReward() {
    const acceptAction = this.removeRewardEvent.bind(this);
    const failAction = () => {
      if (this.isInfoModal) {
        setTimeout(() => {
          this.dialogService.openRewardInfoModal(
            this.reward,
            this.availableFamilies,
            this.availableBusinessLevels,
            acceptAction
          );
        }, 150);
      }
    };
    const deleteHandler = this.isInfoModal ? this.deleteHandler : undefined;
    if (this.isInfoModal) {
      this.closeDialog.emit(false);
      setTimeout(() => {
        this.dialogService.openDeleteRewardModal(
          this.reward,
          acceptAction,
          failAction,
          deleteHandler
        );
      }, 150);
    } else {
      this.dialogService.openDeleteRewardModal(
        this.reward,
        acceptAction,
        failAction,
        deleteHandler
      );
    }
  }

  /**
   * redirects the user to the add rewards screen
   * @param {RewardResponseDTO} reward - The reward to edit.
   */
  goToRewardEdit(reward: RewardResponseDTO) {
    if (this.isInfoModal) {
      this.closeDialog.emit(false);
    }
    this.router.navigate([RoutesKeys.REWARD_DETAIL, reward.id], {
      state: { rewardData: reward, previousPage: RoutesKeys.REWARDS },
    });
  }

  /**
   * Emits an event to remove the reward.
   */
  removeRewardEvent() {
    this.removeReward.emit(this.reward);
  }

  /**
   * Emits the card click event.
   */
  handleClick() {
    this.cardClick.emit(this.reward);
  }
}
